/**
 * The turn's shape.
 *
 * `occupy` is not a step in the chain; it is a state an attack drops into on a
 * conquest and climbs out of once armies have advanced. Everything else runs
 * in order, with the card draw skipped unless something was conquered.
 */

import type { SeatId } from '../ids.js';
import type { GameState, Phase } from '../state/types.js';

/** The phases a turn walks through, in order. */
export const TURN_PHASES: readonly Phase[] = [
  'reinforceTrade',
  'reinforcePlace',
  'attack',
  'fortify',
  'cardDraw',
  'audit',
];

/**
 * The phase that follows the current one, or `null` when the turn is over.
 * Returns `occupy` while a conquest is waiting for armies, since nothing else
 * may happen until then.
 */
export function nextPhase(state: GameState): Phase | null {
  if (state.turn?.pendingOccupation != null) return 'occupy';

  switch (state.phase) {
    case 'occupy':
      // Armies have moved; back to attacking.
      return 'attack';
    case 'reinforceTrade':
      return 'reinforcePlace';
    case 'reinforcePlace':
      return 'attack';
    case 'attack':
      return 'fortify';
    case 'fortify':
      // Exactly one card per turn, and only for a turn that took ground.
      return state.turn?.conqueredThisTurn === true ? 'cardDraw' : 'audit';
    case 'cardDraw':
      return 'audit';
    case 'audit':
      return null;
    default:
      return null;
  }
}

export function isTurnOver(state: GameState): boolean {
  return state.phase === 'audit' && state.turn?.pendingOccupation == null;
}

/** Seats still in the game and able to take a turn. */
export function activeSeats(state: GameState): readonly SeatId[] {
  return state.seatOrder.filter((id) => {
    const seat = state.seats.find((s) => s.id === id);
    return seat !== undefined && !seat.eliminated && !seat.isNeutral;
  });
}

/**
 * Whose turn comes after `after`. Skips eliminated players and the two-player
 * neutral, which owns ground but never takes a turn.
 */
export function nextActiveSeat(state: GameState, after: SeatId): SeatId | null {
  const active = activeSeats(state);
  if (active.length === 0) return null;

  const position = state.seatOrder.indexOf(after);
  if (position === -1) return active[0] ?? null;

  for (let step = 1; step <= state.seatOrder.length; step += 1) {
    const candidate = state.seatOrder[(position + step) % state.seatOrder.length]!;
    if (active.includes(candidate)) return candidate;
  }
  return null;
}

/** The winner under the configured condition, or `null` if play continues. */
export function winnerOf(state: GameState): SeatId | null {
  if (state.rules?.victory.condition !== 'conquest') return null;
  // Nobody can win a game that has not begun. Without this, recording the
  // board one territory at a time during setup declares whoever was entered
  // first the winner, because they hold every territory the app knows about.
  if (state.status !== 'playing') return null;

  const owners = new Set(
    Object.values(state.territories)
      .map((territory) => territory.owner)
      .filter((owner): owner is SeatId => owner !== null),
  );
  if (owners.size !== 1) return null;

  const [only] = [...owners];
  const seat = state.seats.find((s) => s.id === only);
  // A neutral army holding the board is not a winner.
  return seat && !seat.isNeutral ? only! : null;
}
